import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    ConfiguratorTableService,
    EngineDataQueryService,
    FieldTable,
    FieldTableDto,
    ObjectQueryFilter,
    Ordering,
    Table
} from '@tj/tj-dmp-lib-api';
import {FilterMetadata, LazyLoadEvent, MenuItem} from 'primeng/api';
import * as fileSaver from 'file-saver';
import {Field, Filter} from '../../../components/filter/filter.type';
import {TranslateService} from '@ngx-translate/core';
import {FilterBuilderService} from '../../../services/filter-builder.service';
import {parseType} from '../../../components/filter/filter.utils';
import {DialogService} from 'primeng/dynamicdialog';
import {RowEditorComponent} from './row-editor/row-editor.component';
import {FilterSaveListService} from "../../../components/filter/filter-save-list/filter-save-list.service";

interface Params {
    start: number;
    end: number;
    sort: string[];
    filters: {
        [s: string]: FilterMetadata;
    };
}

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'dmp-table-data',
    templateUrl: './table-data.component.html',
    styleUrls: ['./table-data.component.scss'],
    providers: [DialogService],
    standalone: false
})
export class TableDataComponent implements OnInit {
    breadcrumb: MenuItem[] = [
        {label: this.translate.instant('menu.data-center.label')},
        {label: this.translate.instant('menu.data-center.tables'), routerLink: '/h/tables'},
        {label: this.translate.instant('tagsEvent.detailsMenuLabel')},
    ];
    // FIXME à renomer `table`
    database: string;
    group: string;

    data: unknown[] = [];
    totalRecords: number;
    currentData: [number, number] = [null, null];

    // Liste des toutes les colonnes disponibles
    allColumns: FieldTableDto[] = [];
    // Liste des colonnes affichés dans le tableau
    showColumns: FieldTableDto[] = [];

    loading = false;

    params: Params = null;
    filters: Filter[] = [];
    orderings: Array<Ordering> = [];

    fields = [] as Field[];
    private table: Table;

    constructor(
        protected readonly route: ActivatedRoute,
        protected readonly engineDataQueryService: EngineDataQueryService,
        protected readonly tableResourceService: ConfiguratorTableService,
        protected readonly translate: TranslateService,
        protected readonly filterBuilderService: FilterBuilderService,
        protected readonly dialogService: DialogService,
        protected readonly filterSaveService: FilterSaveListService
    ) {
    }

    hasFilters() {
        if (!this.params?.filters) {
            return false;
        }
        return Object.values(this.params.filters).findIndex((el) => el.value ?? false === false) >= 0;
    }

    ngOnInit(): void {
        this.filterSaveService.clear();
        // FIXME à renomer `table`
        this.database = this.route.snapshot.paramMap.get('database');
        this.group = this.route.snapshot.paramMap.get('group');
        this.tableResourceService
            .getTableUsingGET(this.group, this.database)
            .subscribe(async (result) => {
                this.table = result;
                this.allColumns = result.fields.map(c => c as FieldTableDto);
                this.allColumns.forEach((col) => {
                    col.table = result.code;
                    col.schema = result.schema;
                });
                this.fields = await Promise.all(this.allColumns.map(async (column) => ({
                    name: column.code,
                    label: column.label ?? column.code,
                    variableType: parseType(column.categoryField, column.type),
                    valueType: column.categoryField
                }))) as Field[];
                this.showColumns = this.allColumns.slice(0, 5);
                this.loadAll();
            });
    }

    loadAll(event?: LazyLoadEvent | undefined) {
        const start = event ? Math.round(event.first / event.rows) : 0;
        const end = event ? event.rows : 10;
        const sort = event && event.multiSortMeta ? event.multiSortMeta.map((e) => e.field) : undefined;
        const filters = event && event.filters ? event.filters : undefined;
        this.params = {start, end, sort, filters};
        this.currentData = event
            ? [event.first, event.first + event.rows]
            : [0, 10];
        // Convert UI sort metadata to backend Ordering format
        if (event && event.multiSortMeta) {
            this.orderings = this.filterBuilderService.buildOrderings(event.multiSortMeta);
        } else {
            this.orderings = [];
        }
        this.postQuery();
    }

    postQuery() {
        this.loading = true;
        const objectQueryFilter: ObjectQueryFilter = {
            orderings: this.orderings,
            constraint: this.filterBuilderService.buildQuery(this.database, this.filters),
        };
        this.engineDataQueryService.getQueryDataByTableUsingPOST(this.group, this.params.start, objectQueryFilter,
            this.params.end, this.database, 'response')
            // this.queryService.sendQuery(this.group, query, this.params.start, this.params.end, this.params.sort)
            .subscribe((data) => {
                this.totalRecords = +data.headers.get('x-total-count');
                this.data = data.body.map((el) => el.rows);
                this.loading = false;
            });
    }

    needTooltip(text: string | unknown) {
        if (typeof text === 'string') {
            return text.split(/[\n ]/).filter(el => el.length > 25).length > 0;
        }
        return false;
    }

    download() {
        const objectQueryFilter: ObjectQueryFilter = {
            orderings: this.orderings,
            constraint: this.filterBuilderService.buildQuery(this.database, this.filters)
        };
        this.engineDataQueryService.downloadQueryByTableUsingPOST(this.group, objectQueryFilter, this.database, 'response')
            // this.queryService.downloadQuery(query)
            .subscribe((response) => {
                const filename = response.headers.get('filename');

                this.saveFile(response.body as string, filename);
            });
    }

    saveFile(data: BlobPart, filename?: string) {
        const blob = new Blob([data], {type: 'text/csv; charset=utf-8'});
        fileSaver.saveAs(blob, filename);
    }

    handleFilter(filters: Filter[]) {
        this.filters = filters;
        this.postQuery();
    }

    doDetail(row: any) {
        this.dialogService.open(RowEditorComponent, {data: {
                table: this.table,
                data: row
            },
            header: this.translate.instant('tagsEvent.detailsMenuLabel'),
            closable: true,
            width: '70%',
            contentStyle: {'max-height': '70vh', overflow: 'auto'}});
    }
}
